import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, Plus, Users, DollarSign,
  Handshake, Filter, RefreshCw, LayoutDashboard, BellRing,
} from "lucide-react";
import { API, useAuth } from "../../App";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { FilterBar } from "@/components/admin/FilterBar";
import { MultiSelectFilter } from "@/components/admin/MultiSelectFilter";
import { useUrlFilters } from "@/hooks/useUrlFilters";
import { useFilterOptions } from "@/hooks/useFilterOptions";

/**
 * Partners CRM — Companies list (Phase 1).
 *
 * Reads `/api/partners/enums` once for dropdowns, then paginated GET on
 * `/api/partners/companies` with server-side filtering. Create form is a
 * shadcn Dialog. Row click routes to the detail page.
 */

const STATUS_TONE = {
  Prospect: "bg-slate-100 text-slate-700",
  Contacted: "bg-blue-100 text-blue-700",
  "Meeting Scheduled": "bg-indigo-100 text-indigo-700",
  Partner: "bg-emerald-100 text-emerald-700",
  Active: "bg-emerald-100 text-emerald-700",
  Dormant: "bg-amber-100 text-amber-700",
  Inactive: "bg-red-100 text-red-700",
};

export default function PartnersCompanies() {
  const { token } = useAuth();
  const { toast } = useToast();

  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [remindersSummary, setRemindersSummary] = useState(null);
  const { options: enums } = useFilterOptions();

  // Global filter state — URL-synced + session-persistent
  const FILTER_CONFIG = useMemo(() => ({
    q: { type: "string" },
    industry: { type: "multi" },
    company_status: { type: "multi" },
    partner_type: { type: "multi" },
    submarket: { type: "multi" },
    assigned_agent: { type: "multi" },
    recruiting_volume: { type: "multi" },
    rel_score_min: { type: "number" },
    rel_score_max: { type: "number" },
    created_from: { type: "string" },
    created_to: { type: "string" },
  }), []);
  const { filters, setFilter, reset, toParams, activeCount } = useUrlFilters(
    "partners-companies", FILTER_CONFIG,
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    industry: "",
    partner_type: "",
    houston_submarket: "",
    employee_count: "",
    company_status: "Prospect",
    website: "",
    notes: "",
  });

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Load dashboard + reminders summary once
  useEffect(() => {
    (async () => {
      try {
        const [d, rs] = await Promise.all([
          axios.get(`${API}/partners/dashboard`, { headers: authHeader }),
          axios.get(`${API}/partners/reminders-summary`, { headers: authHeader }),
        ]);
        setDashboard(d.data);
        setRemindersSummary(rs.data);
      } catch (err) {
        toast({ title: "Failed to load", description: err?.response?.data?.detail || err.message, variant: "destructive" });
      }
    })();
     
  }, [authHeader, toast]);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, ...toParams() };
      const res = await axios.get(`${API}/partners/companies`, { headers: authHeader, params });
      setCompanies(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast({ title: "Failed to load companies", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
     
  }, [toParams, authHeader, toast]);

  useEffect(() => {
    const t = setTimeout(fetchCompanies, 250);
    return () => clearTimeout(t);
  }, [fetchCompanies]);

  // CSV URL respects all active filters
  const csvUrl = useMemo(() => {
    const params = new URLSearchParams(toParams()).toString();
    return `${API}/partners/companies/export.csv${params ? `?${params}` : ""}`;
  }, [toParams]);

  const submitCreate = async () => {
    if (!form.company_name.trim()) {
      toast({ title: "Company name required", variant: "destructive" });
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form };
      if (payload.employee_count) payload.employee_count = parseInt(payload.employee_count, 10);
      else delete payload.employee_count;
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await axios.post(`${API}/partners/companies`, payload, { headers: authHeader });
      toast({ title: "Company created" });
      setCreateOpen(false);
      setForm({
        company_name: "", industry: "", partner_type: "", houston_submarket: "",
        employee_count: "", company_status: "Prospect", website: "", notes: "",
      });
      fetchCompanies();
      // Refresh dashboard totals
      axios.get(`${API}/partners/dashboard`, { headers: authHeader }).then((d) => setDashboard(d.data)).catch(() => {});
    } catch (err) {
      toast({ title: "Create failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="partners-companies-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-[#DE6E4B]" />
            Partners CRM
          </h1>
          <p className="text-slate-600 text-sm mt-1">
            B2B partnerships — HR, recruiters, corporate mobility, and staffing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            asChild
            data-testid="partners-exec-dashboard-btn"
          >
            <Link to="/admin/partners/dashboard">
              <LayoutDashboard className="w-4 h-4 mr-2" /> Executive Dashboard
            </Link>
          </Button>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#DE6E4B] hover:bg-[#c85d3f]"
              data-testid="partners-add-company-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="partners-create-dialog">
            <DialogHeader>
              <DialogTitle>Add Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Company name *</Label>
                <Input
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  data-testid="partners-form-company-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Industry</Label>
                  <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                    <SelectTrigger data-testid="partners-form-industry"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(enums?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Partner type</Label>
                  <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
                    <SelectTrigger data-testid="partners-form-partner-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(enums?.partner_types || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Houston submarket</Label>
                  <Select value={form.houston_submarket} onValueChange={(v) => setForm({ ...form, houston_submarket: v })}>
                    <SelectTrigger data-testid="partners-form-submarket"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {(enums?.houston_submarkets || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Employee count</Label>
                  <Input
                    type="number"
                    value={form.employee_count}
                    onChange={(e) => setForm({ ...form, employee_count: e.target.value })}
                    data-testid="partners-form-employees"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={form.company_status} onValueChange={(v) => setForm({ ...form, company_status: v })}>
                    <SelectTrigger data-testid="partners-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(enums?.company_statuses || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                    placeholder="https://…"
                    data-testid="partners-form-website"
                  />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="partners-form-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                className="bg-[#DE6E4B] hover:bg-[#c85d3f]"
                onClick={submitCreate}
                disabled={creating}
                data-testid="partners-form-submit"
              >
                {creating ? "Creating…" : "Create Company"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Reminders due strip (Phase 3) */}
      {remindersSummary && (remindersSummary.overdue > 0 || remindersSummary.due_today > 0) && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-6" data-testid="partners-reminders-strip">
          <BellRing className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="text-sm text-amber-800">
            {remindersSummary.overdue > 0 && (
              <span className="font-semibold" data-testid="partners-reminders-overdue">{remindersSummary.overdue} overdue reminder{remindersSummary.overdue === 1 ? "" : "s"}</span>
            )}
            {remindersSummary.overdue > 0 && remindersSummary.due_today > 0 && <span> · </span>}
            {remindersSummary.due_today > 0 && (
              <span data-testid="partners-reminders-today">{remindersSummary.due_today} due today</span>
            )}
            {remindersSummary.next?.[0] && (
              <span className="text-amber-700"> — next: {remindersSummary.next[0].title} ({remindersSummary.next[0].due_date})</span>
            )}
          </div>
        </div>
      )}

      {/* KPI cards */}
      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <KpiCard icon={Building2} label="Companies" value={dashboard.total_companies} tone="text-blue-700 bg-blue-50" />
          <KpiCard icon={Users} label="Contacts" value={dashboard.total_contacts} tone="text-indigo-700 bg-indigo-50" />
          <KpiCard icon={Handshake} label="Active Partners" value={dashboard.active_partners} tone="text-emerald-700 bg-emerald-50" />
          <KpiCard icon={DollarSign} label="Commission" value={`$${(dashboard.commission_earned || 0).toLocaleString()}`} tone="text-[#DE6E4B] bg-orange-50" />
        </div>
      )}

      {/* Filters — global multi-select, URL synced */}
      <FilterBar
        searchValue={filters.q}
        onSearchChange={(v) => setFilter("q", v)}
        searchPlaceholder="Search company name…"
        onReset={reset}
        activeCount={activeCount}
        csvUrl={csvUrl}
        csvFilename="partners_companies.csv"
        testId="partners-companies-filters"
      >
        <MultiSelectFilter
          label="Status"
          options={enums?.company_statuses || []}
          value={filters.company_status || []}
          onChange={(v) => setFilter("company_status", v)}
          testId="filter-status"
        />
        <MultiSelectFilter
          label="Industry"
          options={enums?.industries || []}
          value={filters.industry || []}
          onChange={(v) => setFilter("industry", v)}
          testId="filter-industry"
        />
        <MultiSelectFilter
          label="Partner Type"
          options={enums?.partner_types || []}
          value={filters.partner_type || []}
          onChange={(v) => setFilter("partner_type", v)}
          testId="filter-partner-type"
        />
        <MultiSelectFilter
          label="Submarket"
          options={enums?.houston_submarkets || []}
          value={filters.submarket || []}
          onChange={(v) => setFilter("submarket", v)}
          testId="filter-submarket"
        />
        <MultiSelectFilter
          label="Agent"
          options={enums?.assigned_agents || []}
          value={filters.assigned_agent || []}
          onChange={(v) => setFilter("assigned_agent", v)}
          testId="filter-agent"
        />
        <MultiSelectFilter
          label="Recruiting Volume"
          options={(enums?.recruiting_volumes || []).map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }))}
          value={filters.recruiting_volume || []}
          onChange={(v) => setFilter("recruiting_volume", v)}
          testId="filter-recruiting"
        />
        <div className="flex items-center gap-1" data-testid="filter-rel-score">
          <span className="text-xs text-slate-500">Score</span>
          <Input
            type="number"
            min={0}
            max={100}
            value={filters.rel_score_min ?? ""}
            onChange={(e) => setFilter("rel_score_min", e.target.value === "" ? "" : Number(e.target.value))}
            className="h-9 w-16"
            placeholder="min"
            data-testid="filter-rel-score-min"
          />
          <span className="text-xs text-slate-400">–</span>
          <Input
            type="number"
            min={0}
            max={100}
            value={filters.rel_score_max ?? ""}
            onChange={(e) => setFilter("rel_score_max", e.target.value === "" ? "" : Number(e.target.value))}
            className="h-9 w-16"
            placeholder="max"
            data-testid="filter-rel-score-max"
          />
        </div>
        <div className="flex items-center gap-1" data-testid="filter-created-range">
          <span className="text-xs text-slate-500">Created</span>
          <Input
            type="date"
            value={filters.created_from || ""}
            onChange={(e) => setFilter("created_from", e.target.value)}
            className="h-9 w-36"
            data-testid="filter-created-from"
          />
          <span className="text-xs text-slate-400">–</span>
          <Input
            type="date"
            value={filters.created_to || ""}
            onChange={(e) => setFilter("created_to", e.target.value)}
            className="h-9 w-36"
            data-testid="filter-created-to"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={fetchCompanies}
          data-testid="partners-refresh-btn"
          aria-label="Refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </FilterBar>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Industry</th>
                <th className="text-left px-4 py-3">Partner Type</th>
                <th className="text-left px-4 py-3">Submarket</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Last Contact</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Loading…</td></tr>
              )}
              {!loading && companies.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">
                  No companies yet. Click <b>Add Company</b> to get started.
                </td></tr>
              )}
              {!loading && companies.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/partners/${c.id}`}
                      className="font-semibold text-slate-900 hover:text-[#DE6E4B]"
                      data-testid={`partners-row-${c.id}`}
                    >
                      {c.company_name}
                    </Link>
                    {c.website && (
                      <div className="text-xs text-slate-400 truncate max-w-xs">{c.website}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{c.industry || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.partner_type || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{c.houston_submarket || "—"}</td>
                  <td className="px-4 py-3">
                    <Badge
                      variant="outline"
                      className={`${STATUS_TONE[c.company_status] || "bg-slate-100 text-slate-700"} border-transparent`}
                    >
                      {c.company_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {c.last_contact ? new Date(c.last_contact).toLocaleDateString() : "Never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <span>{total} total {total === 1 ? "company" : "companies"}</span>
          <span className="flex items-center gap-1"><Filter className="w-3 h-3" /> Server-side filtered</span>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
          <div className="text-xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );
}
